"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.venueProblems = venueProblems;
const isFiniteVec2 = (v) => Number.isFinite(v.x) && Number.isFinite(v.y);
const isFiniteVec3 = (v) => isFiniteVec2(v) && Number.isFinite(v.z);
const isFraction = (n) => Number.isFinite(n) && n >= 0 && n <= 1;
const isRange = ([lo, hi]) => Number.isFinite(lo) && Number.isFinite(hi) && lo < hi;
const failing = (checks) => checks.filter(([ok]) => !ok).map(([, problem]) => problem);
function featureProblems(id, feature) {
    const shape = (() => {
        switch (feature.kind) {
            case "point":
                return isFiniteVec2(feature.at);
            case "segment":
                return isFiniteVec2(feature.from) && isFiniteVec2(feature.to);
            case "polygon":
                return feature.points.length >= 3 && feature.points.every(isFiniteVec2);
            case "circle":
                return isFiniteVec2(feature.at) && Number.isFinite(feature.radius) && feature.radius > 0;
            default:
                return false;
        }
    })();
    const { z } = feature;
    return failing([
        [shape, `stage feature ${id} must be a point, segment, polygon or circle with finite coordinates`],
        [z === undefined || (Number.isFinite(z.min) && Number.isFinite(z.max) && z.min <= z.max), `stage feature ${id}'s z range must be finite and ordered`],
    ]);
}
function stageProblems(stage) {
    return [
        ...failing([
            [isRange(stage.extents.x) && isRange(stage.extents.y), "stage extents must be finite and non-empty"],
            [Object.hasOwn(stage.features, "center") && stage.features.center.kind === "point", "the stage must have a center point"],
        ]),
        ...Object.entries(stage.features).flatMap(([id, feature]) => featureProblems(id, feature)),
    ];
}
function responseProblems(id, response) {
    switch (response.kind) {
        case "absorb":
            return [];
        case "bounce":
            return failing([
                [isFraction(response.restitution), `panel ${id}'s restitution ${response.restitution} must be in [0, 1]`],
                [isFraction(response.tangential), `panel ${id}'s tangential ${response.tangential} must be in [0, 1]`],
            ]);
        default:
            return [`panel ${id}'s response must bounce or absorb`];
    }
}
function panelProblems(panel) {
    const { id, from, to, bottom, top, topAtEnds } = panel;
    return [
        ...failing([
            [["net", "wall", "goal"].includes(panel.kind), `panel ${id}'s kind ${panel.kind} must be net, wall or goal`],
            [isFiniteVec2(from) && isFiniteVec2(to), `panel ${id}'s ends must be finite`],
            [from.x !== to.x || from.y !== to.y, `panel ${id} must run between two different points`],
            [Number.isFinite(bottom) && Number.isFinite(top) && top > bottom, `panel ${id}'s top ${top} must be above its bottom ${bottom}`],
            [topAtEnds === undefined || (Number.isFinite(topAtEnds) && topAtEnds > bottom), `panel ${id}'s top at the ends ${topAtEnds} must be above its bottom ${bottom}`],
            [["both", "left", "right"].includes(panel.faces), `panel ${id} must face both, left or right`],
        ]),
        ...responseProblems(id, panel.response),
    ];
}
function arenaProblems(arena) {
    const ids = arena.panels.map((panel) => panel.id);
    const repeated = [...new Set(ids.filter((id, i) => ids.indexOf(id) !== i))];
    return [
        ...failing([
            [isRange(arena.bounds.x) && isRange(arena.bounds.y), "arena bounds must be finite and non-empty"],
            [isFraction(arena.ground.restitution), `ground restitution ${arena.ground.restitution} must be in [0, 1]`],
            [repeated.length === 0, `panel ids must be unique: ${repeated.join(", ")} repeat`],
        ]),
        ...arena.panels.flatMap(panelProblems),
    ];
}
function frameProblems({ localization, referencePoints }) {
    const unfinite = Object.entries(referencePoints)
        .filter(([, p]) => !isFiniteVec3(p))
        .map(([name]) => name);
    return failing([
        [isFiniteVec3(localization.origin), "the localization origin must be finite"],
        [typeof localization.flipY === "boolean", "the localization frame must say whether y is flipped"],
        [Number.isFinite(localization.unitsPerMeter) && localization.unitsPerMeter > 0, `localization units per meter ${localization.unitsPerMeter} must be positive`],
        [unfinite.length === 0, `reference points must be finite: ${unfinite.join(", ")} are not`],
    ]);
}
/** Everything wrong with `venue` as a place to play `sport`. */
function venueProblems(venue, sport) {
    return [
        ...failing([[venue.sport === sport, `venue ${venue.id} is for ${venue.sport}`]]),
        ...stageProblems(venue.stage),
        ...arenaProblems(venue.arena),
        ...frameProblems(venue.frame),
    ];
}
